import { IBulkQuoteOfferingReqSchema } from '@monetize/types/app';
import isNil from 'lodash/isNil';
import {
  Control,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { MBox, MFlex } from '../../../../../components/Monetize';
import { BillingFrequency } from './BillingFrequency';
import { BulkRates } from './BulkRates';
import {
  cacheBillingFrequency,
  clearCachedBillingFrequency,
} from './utils/billingFrequencyCacheUtil';
import {
  areAllOfferingsOneTime,
  resolveBulkRates,
} from './utils/bulkOfferingUtils';

interface BulkReviewRatesProps {
  control: Control<IBulkQuoteOfferingReqSchema>;
  setValue: UseFormSetValue<IBulkQuoteOfferingReqSchema>;
  getValues: UseFormGetValues<IBulkQuoteOfferingReqSchema>;
  watch: UseFormWatch<IBulkQuoteOfferingReqSchema>;
  disabled?: boolean;
}

/**
 * Review step of the bulk add flow: the billing frequency for every offering,
 * and the rate each offering resolved to for it.
 */
export const BulkReviewRates = ({
  control,
  setValue,
  getValues,
  watch,
  disabled,
}: BulkReviewRatesProps) => {
  const offerings = watch('offerings');
  const billingFrequency = watch('billingFrequency');
  const customBillingFrequency = watch('customBillingFrequency');

  const applyBillingFrequency = (
    nextBillingFrequency: string,
    nextCustomBillingFrequency: number | null,
  ) => {
    // Rebuild the rate ids first. The auto-select in `BulkRates` leaves a row
    // without a matching rate untouched, so the rate resolved for the previous
    // frequency would stay selected next to the "no rate" error.
    setValue(
      'bulkRate',
      resolveBulkRates(
        getValues('offerings'),
        nextBillingFrequency,
        getValues('bulkRate'),
      ),
    );
    setValue('billingFrequency', nextBillingFrequency);
    setValue('customBillingFrequency', nextCustomBillingFrequency);
  };

  const handleFrequencySelect = (nextBillingFrequency: string) => {
    applyBillingFrequency(nextBillingFrequency, null);
    // Any clears the last-used frequency, so the next bulk add starts on Annual
    if (nextBillingFrequency) {
      cacheBillingFrequency(nextBillingFrequency);
    } else {
      clearCachedBillingFrequency();
    }
  };

  // Custom values are never cached
  const handleCustomFrequencyChange = (
    nextCustomBillingFrequency: number | null,
  ) => {
    applyBillingFrequency(
      isNil(nextCustomBillingFrequency)
        ? ''
        : String(nextCustomBillingFrequency),
      nextCustomBillingFrequency,
    );
  };

  return (
    <MFlex direction="column" gap={5}>
      {!areAllOfferingsOneTime(offerings) && (
        <BillingFrequency
          billingFrequency={billingFrequency}
          customBillingFrequency={customBillingFrequency}
          disabled={disabled}
          onFrequencySelect={handleFrequencySelect}
          onCustomFrequencyChange={handleCustomFrequencyChange}
        />
      )}
      <MBox>
        <BulkRates
          setValue={setValue}
          watch={watch}
          control={control}
          disabled={disabled}
        />
      </MBox>
    </MFlex>
  );
};
